import { useEffect, useRef } from "react";
import { product, reviews } from "@/mocks/product";

function StarIcon({ filled }: { filled: boolean }) {
  if (filled) {
    return (
      <i className="ri-star-fill w-3.5 h-3.5 flex items-center justify-center text-secondary-500 text-sm" />
    );
  }
  return (
    <i className="ri-star-line w-3.5 h-3.5 flex items-center justify-center text-secondary-500 text-sm" />
  );
}

export default function ReviewsSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            el.classList.add("animate-fade-in");
            observer.unobserve(el);
          }
        });
      },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const marqueeReviews = [...reviews, ...reviews];

  return (
    <section ref={sectionRef} className="py-12 md:py-16 bg-background-50 overflow-hidden">
      <style>{`
        @keyframes marquee-scroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>

      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="text-center mb-8 md:mb-10">
          <div className="inline-flex items-center gap-2 mb-3">
            <i className="ri-star-fill w-4 h-4 flex items-center justify-center text-secondary-500" />
            <span className="text-sm font-medium text-secondary-800 tracking-wide uppercase">
              Real Reviews
            </span>
          </div>
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-accent-800 mb-4">
            Loved by our customers
          </h2>
          <div className="inline-flex items-center gap-2 px-5 py-2 bg-secondary-100 rounded-full">
            <span className="text-lg font-bold text-secondary-900">
              {product.rating}
            </span>
            <div className="flex items-center gap-0.5">
              {[1, 2, 3, 4, 5].map((i) => (
                <StarIcon key={i} filled={i <= Math.floor(product.rating)} />
              ))}
            </div>
            <span className="text-sm text-secondary-800">
              · Trusted on {product.ratingSource}
            </span>
          </div>
        </div>
      </div>

      <div className="relative group">
        <div
          className="flex gap-5 md:gap-6 group-hover:[animation-play-state:paused]"
          style={{
            animation: "marquee-scroll 40s linear infinite",
            width: "max-content",
          }}
        >
          {marqueeReviews.map((review, index) => (
            <div
              key={`${review.id}-${index}`}
              className="flex-shrink-0 w-[310px] md:w-[370px] bg-background-100 rounded-xl p-6 md:p-7 shadow-sm border border-background-200/70 transition-shadow duration-300 hover:shadow-md"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-secondary-100 flex items-center justify-center text-secondary-600 font-semibold text-sm flex-shrink-0">
                  {review.name.charAt(0)}
                </div>
                <div>
                  <p className="font-semibold text-sm text-accent-800">
                    {review.name}
                  </p>
                  <p className="text-xs text-foreground-500">{review.date}</p>
                </div>
              </div>
              <div className="flex items-center gap-0.5 mb-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <StarIcon key={i} filled={i <= review.rating} />
                ))}
              </div>
              <p className="text-foreground-600 text-sm leading-relaxed">
                &ldquo;{review.text}&rdquo;
              </p>
            </div>
          ))}
        </div>

        {/* Fade edges */}
        <div className="absolute left-0 top-0 bottom-0 w-12 md:w-24 bg-gradient-to-r from-background-50 to-transparent pointer-events-none z-10" />
        <div className="absolute right-0 top-0 bottom-0 w-12 md:w-24 bg-gradient-to-r from-transparent to-background-50 pointer-events-none z-10" />
      </div>
    </section>
  );
}